import { action, makeAutoObservable, observable, runInAction } from "mobx"
import { initializeApp } from "firebase/app"
import { getAuth, signOut } from "firebase/auth"
import { notification } from "antd"

import { GetCurrentLoginInformations } from "@services/session/dto/getCurrentLoginInformations"
import sessionService from "@services/session/sessionService"
import userService from "@services/administrator/user/userService"
import { compressImage, notifyWarning } from "@lib/helper"
import { LNotification } from "@lib/abpUtility"
import AppConsts, { firebaseConfig } from "@lib/appconst"
import tokenAuthService from "@services/tokenAuth/tokenAuthService"
import type { HostSettingConfiguration } from "@models/global"
import { AppSettingConfiguration } from "@models/global"
import { userLayout } from "@components/Layout/Router/router.menu"
import projectService from "@services/projects/projectService"
const { authorization } = AppConsts

const defaultAvatar = "/assets/images/logo.svg"
class SessionStore {
  @observable currentLogin: GetCurrentLoginInformations =
    new GetCurrentLoginInformations()
  @observable profilePicture!: string
  @observable appSettingConfiguration!: AppSettingConfiguration
  @observable ownProjects: any = []
  @observable project: any = {}
  @observable hostSetting!: HostSettingConfiguration
  @observable defaultProjectId: number | null = null
  @observable isSettingDefault: boolean = false

  constructor() {
    makeAutoObservable(this)
    this.project = {}
    this.appSettingConfiguration = new AppSettingConfiguration()
  }

  get projectId() {
    return parseInt(
      (localStorage.getItem(authorization.projectId) || 0).toString()
    )
  }

  @action async updateUsername(body) {
    await sessionService.updateUsername(body)
    await this.getCurrentLoginInformations()
  }
  @action async getHostSetting() {
    const res = await sessionService.getHostSetting()
    this.hostSetting = res
    return res
  }
  @action async changeHostSetting(body) {
    const res = await sessionService.changeHostSetting(body)
    this.hostSetting = res
    return res
  }

  @action
  async getCurrentLoginInformations() {
    const result = await sessionService.getCurrentLoginInformations()
    this.currentLogin = result
  }

  @action
  async getWebConfiguration() {
    const result = await sessionService.getWebConfiguration()
    this.appSettingConfiguration = result
  }

  @action
  async getMyProfilePicture() {
    const result = await userService.getMyProfilePicture()
    runInAction(() => {
      this.profilePicture = result || defaultAvatar
    })
  }

  @action
  async uploadMyProfilePicture(file) {
    const compressedImage = await compressImage(file, 1024)
    const result = await userService.uploadProfilePicture(compressedImage)
    return result
  }

  @action
  async updateMyProfilePicture(data) {
    await userService.updateMyProfilePicture(data)
    await this.getMyProfilePicture()
  }

  @action
  async updateMyProfile(data) {
    await userService.updateMyProfile(data)
    // if ((this.currentLogin.user.phoneNumber !== data.phoneNumber) || (this.currentLogin.user.emailAddress !== data.emailAddress)) {
    //   await staffService.sendActiveEmail(this.currentLogin.user.id)
    // }
    this.currentLogin.user = { ...this.currentLogin.user, ...data }
  }

  @action
  async logout() {
    const app = initializeApp(firebaseConfig)
    const auth = getAuth(app)
    signOut(auth)
      .then(() => {
        // Sign-out successful.
      })
      .catch((error) => {
      })
    abp.utils.deleteCookie(
      AppConsts.authorization.encrptedAuthTokenName,
      abp.appPath
    )
    abp.utils.deleteCookie(AppConsts.authorization.projectId, abp.appPath)

    localStorage.clear()
    sessionStorage.clear()
    abp.auth.clearToken()

    window.location.href = userLayout.accountLogin.path
  }

  @action
  async setDefaultProject(projectId: number | null): Promise<void> {
    if (this.isSettingDefault) return

    const previousDefaultProjectId = this.defaultProjectId
    this.isSettingDefault = true

    try {
      const timeoutMs = 10000
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

      await Promise.race([
        sessionService.setDefaultProject(projectId),
        new Promise<never>((_, reject) => {
          controller.signal.addEventListener("abort", () =>
            reject(new Error("Request timed out"))
          )
        }),
      ])

      clearTimeout(timeoutId)

      // On success: update defaultProjectId to the new value
      this.defaultProjectId = projectId
    } catch {
      // On failure/timeout: revert to previous value and show error notification
      this.defaultProjectId = previousDefaultProjectId
      notification.error({
        message: "Error",
        description: "Failed to set default project. Please try again.",
        duration: 5,
      })
    } finally {
      this.isSettingDefault = false
    }
  }

  @action
  async getOwnProjects(_params?: any) {
    try {
      const projects = await projectService.getMyAssignedProjects()
      this.ownProjects = projects

      // Sync defaultProjectId from API response
      this.defaultProjectId = this.currentLogin.defaultProjectId ?? null

      const savedId = this.projectId

      if (savedId) {
        // Priority 1: Manual selection from localStorage wins
        this.project = projects.find((p) => p.id === savedId) ?? {}
      } else if (this.defaultProjectId) {
        // Priority 2: Auto-select default project without reload
        const defaultProject = projects.find(
          (p) => p.id === this.defaultProjectId
        )
        if (defaultProject) {
          this.project = defaultProject
          const cookieExpire = new Date(
            new Date().getTime() + 1000 * 315360000
          ) // 10 years
          localStorage.setItem(
            authorization.projectId,
            String(defaultProject.id)
          )
          abp.utils.setCookieValue(
            authorization.projectId,
            String(defaultProject.id),
            cookieExpire,
            "/",
            undefined,
            { Secure: true, SameSite: "Lax" }
          )
        }
        // If defaultProjectId doesn't match any project, leave unselected
      } else if (projects.length === 1) {
        // Priority 3: Auto-pick when user only has 1 project
        this.setProject(projects[0])
      }
      // Priority 4: No selection — ProjectSelector shows placeholder
    } catch {
      this.ownProjects = []
    }
  }

  /** Lưu project vào cookie + localStorage rồi reload để các module filter tự pick up. */
  @action
  setProject(project: any) {
    const cookieExpire = new Date(new Date().getTime() + 1000 * 315360000) // 10 năm

    if (!project?.id) {
      this.project = {}
      this.defaultProjectId = null
      localStorage.removeItem(authorization.projectId)
      abp.utils.deleteCookie(authorization.projectId, abp.appPath)

      // Fire-and-forget: clear default project on server (optimistic UI)
      sessionService.setDefaultProject(null).catch(() => {
        notifyWarning(
          LNotification("WARNING"),
          LNotification("SAVE_DEFAULT_PROJECT_FAILED")
        )
      })
    } else {
      this.project = project
      localStorage.setItem(authorization.projectId, String(project.id))
      abp.utils.setCookieValue(
        authorization.projectId,
        String(project.id),
        cookieExpire,
        "/",
        undefined,
        { Secure: true, SameSite: "Lax" }
      )
    }

    window.location.reload()
  }

  /** @deprecated Use setProject() for header selector. Keep for LoginProjectPanel compatibility. */
  @action
  async changeProject(project) {
    if (!project) return
    const result = await tokenAuthService
      .switchProject(project.id)
      .finally(() => (this.project = project))
    const tokenExpireDate = new Date(new Date().getTime() + 1000 * 315360000)
    abp.auth.setToken(result.accessToken, tokenExpireDate)
    abp.utils.setCookieValue(
      AppConsts.authorization.encrptedAuthTokenName,
      result.encryptedAccessToken,
      tokenExpireDate,
      "/",
      undefined,
      { Secure: true, SameSite: "Lax", MaxAge: 315360000, path: "/" }
    )
    localStorage.setItem(authorization.projectId, project.id)
  }
}

export default SessionStore
